import React from 'react'
import { spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { EmptyMessage } from '@core/Areas/BetLoop/Components/Common/EmptyMessage'
import { SkeletonLoader } from './Components/SkeletonLoader'
import { PendingTicket } from './Components/PendingTicket'
import { useSharePendingTicketsData } from './hooks/useSharePendingTicketsData'
import { useKambiSharePendingTicketsData } from './hooks/useKambiSharePendingTicketsData'
import { getSingularBetFromKambiCoupon } from '@core/Areas/BetLoop/Components/QuickShare/helpers/getSingularBetFromKambiCoupon'
import type { Ticket } from '@core/Areas/AccountActivity/types'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import type { BetLoopKambiBetPayload } from '@core/Areas/BetLoop/helpers/api/types'
import { isVenueModeTicket } from '@core/Areas/VenueMode/Utils/isVenueModeTicket'
import { useVenueModeRacingEvents } from '@core/Areas/VenueMode/Components/Racing/Data/api'
import { rebuildKambiHistoryCoupon } from '@core/Areas/KambiSports/Player/helpers/rebuildKambiHistoryCoupon'

export const ShareBetSelection: React.FC<{
  ticketsToShare: Set<number>
  kambiTicketsToShare: Set<BetLoopKambiBetPayload>
  onTicketClick(id: number, ticket: Ticket | KambiHistoryCoupon): void
  onEmptyState(isEmpty: boolean): void
}> = ({ onTicketClick, ticketsToShare, kambiTicketsToShare, onEmptyState }) => {
  const {
    kambiTickets,
    isLoading: isKambiLoading,
    isEmptyState: isKambiEmptyState,
  } = useKambiSharePendingTicketsData()

  const {
    racingTickets,
    isLoading: isRacingLoading,
    isEmptyState: isRacingEmptyState,
  } = useSharePendingTicketsData()

  const isEmptyState = isRacingEmptyState && isKambiEmptyState
  const isLoading = isRacingLoading || isKambiLoading

  const { data: venueModeEvents } = useVenueModeRacingEvents()

  const sortedTickets = React.useMemo(() => {
    const entries: TicketEntry[] = []

    kambiTickets?.forEach(ticket => {
      ticket.bets.forEach(bet => {
        if (bet.betStatus !== 'OPEN') return
        const kambiTicket = getSingularBetFromKambiCoupon(ticket, bet.betRef)
        // Sort events in the ticket by event start date to ensure correct ordering in the UI
        const { coupon } = rebuildKambiHistoryCoupon(ticket)
        kambiTicket.bets[0].couponRowIndexes = coupon.couponRows.map(c => c.index)

        entries.push({
          type: 'kambi',
          ticket: kambiTicket,
          bet,
          eventStart: Math.min(
            ...kambiTicket.events.map(e => new Date(e.eventStartDate).getTime())
          ),
        })
      })
    })

    racingTickets?.forEach(ticket => {
      if (isVenueModeTicket(ticket, venueModeEvents)) return
      entries.push({
        type: 'racing',
        ticket,
        eventStart: new Date(
          ticket.updatedEventStartDateTime ?? ticket.eventStartDateTime
        ).getTime(),
      })
    })

    return entries.sort((a, b) => a.eventStart - b.eventStart)
  }, [racingTickets, kambiTickets, venueModeEvents])

  React.useEffect(() => {
    onEmptyState(isEmptyState)
  }, [isEmptyState, onEmptyState])

  if (isEmptyState) {
    return (
      <EmptyMessage
        messageType='tickets'
        title='No Eligible Pending Bets'
        description='Once you place a bet, it’ll show up here - and you’ll be able to share it to Bet Loop.'
      />
    )
  }

  return (
    <Flex flexDirection='column' gap={spacing.smx1}>
      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <>
          {sortedTickets.map(entry => {
            if (entry.type === 'kambi') {
              return (
                <PendingTicket
                  key={entry.bet.betRef}
                  ticket={getSingularBetFromKambiCoupon(entry.ticket, entry.bet.betRef)}
                  isSelected={[...kambiTicketsToShare].some(x => x.betRef === entry.bet.betRef)}
                  onClick={() => onTicketClick(entry.bet.betRef, entry.ticket)}
                />
              )
            }
            return (
              <PendingTicket
                key={entry.ticket.ticketSequence}
                ticket={entry.ticket}
                isSelected={ticketsToShare.has(entry.ticket.ticketSequence)}
                onClick={ticketSeq => onTicketClick(ticketSeq, entry.ticket)}
              />
            )
          })}
        </>
      )}
    </Flex>
  )
}

//=======
// Types
//=======

type RacingEntry = { type: 'racing'; ticket: Ticket; eventStart: number }
type KambiEntry = {
  type: 'kambi'
  ticket: KambiHistoryCoupon
  bet: KambiHistoryCoupon['bets'][0]
  eventStart: number
}
type TicketEntry = RacingEntry | KambiEntry
